import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OTPVerification = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [email, setEmail] = useState("");
  const sentRef = useRef(false);

  useEffect(() => {
    const userEmail = localStorage.getItem("userEmail");
    const verified = localStorage.getItem("userVerified") === "true";
    if (!userEmail) {
      navigate("/login");
      return;
    }
    if (verified) {
      navigate("/dashboard");
      return;
    }
    setEmail(userEmail);
    if (!sentRef.current) {
      sentRef.current = true;
      sendOTP(userEmail, false);
    }
  }, [navigate]);

  useEffect(() => {
    const verifyServer = async () => {
      if (!email) return;
      try {
        const response = await axios.get(`${API}/auth/status`, { params: { email } });
        if (response.data?.is_verified) {
          localStorage.setItem("userVerified", "true");
          navigate("/dashboard");
        }
      } catch {
        // ignore
      }
    };
    verifyServer();
  }, [email, navigate]);

  const sendOTP = async (userEmail, showLoading = true) => {
    try {
      if (showLoading) setResending(true);
      await axios.post(`${API}/auth/send-otp`, { email: userEmail });
      toast.success("OTP sent to your email!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      if (showLoading) setResending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");
    
    if (otpString.length !== 6) {
      toast.error("Please enter complete OTP");
      return;
    }
    if (!/^\d{6}$/.test(otpString)) {
      toast.error("OTP must be 6 digits");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/verify-otp`, {
        email: email,
        otp: otpString
      });
      
      toast.success("Email verified successfully!");
      localStorage.setItem("userVerified", "true");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container auth-page-container">
      <div className="auth-card" data-testid="otp-card">
        <h1>Verify Your Email</h1>
        <p>Enter the OTP sent to {email}</p>

        <form onSubmit={handleSubmit}>
          <div className="otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                maxLength="1"
                className="otp-input"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                data-testid={`otp-input-${index}`}
              />
            ))}
          </div>

          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading}
            data-testid="verify-button"
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </form>

        <div className="link-text">
          Didn't receive OTP?{" "}
          <button
            type="button"
            className="btn-link"
            onClick={() => sendOTP(email, true)}
            disabled={resending}
            data-testid="resend-otp"
          >
            {resending ? "Resending..." : "Resend"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
